import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { employerSignup, employerSignin } from './postJobApi';
import useAuthStore from '../../stores/authStore';

const FREE_DOMAINS = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com',
  'aol.com', 'protonmail.com', 'live.com', 'me.com',
];

function isWorkEmail(email) {
  const domain = email.split('@')[1]?.toLowerCase();
  return domain && !FREE_DOMAINS.includes(domain);
}

function EmployerAuthStep({ onAuthenticated }) {
  const { setAuth } = useAuthStore();
  const [mode, setMode] = useState('signup');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [form, setForm] = useState({
    fullName: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const handleChange = (e) => {
    setError('');
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!isWorkEmail(form.email)) {
      setError('Please use your company work email. Personal providers like Gmail or Outlook are not accepted.');
      return;
    }

    if (mode === 'signup') {
      if (form.password.length < 8) {
        setError('Password must be at least 8 characters.');
        return;
      }
      if (form.password !== form.confirmPassword) {
        setError('Passwords do not match.');
        return;
      }
    }

    try {
      setLoading(true);

      if (mode === 'signup') {
        const { confirmPassword, ...signupPayload } = form;
        const data = await employerSignup(signupPayload);
        setSuccess(data.message || 'Account created. Please verify your work email, then sign in to continue.');
        setMode('signin');
        return;
      }

      const data = await employerSignin({
        email: form.email,
        password: form.password,
      });

      setAuth(data.user, data.accessToken);
      onAuthenticated();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="post-job-card ch-card-enter">
      <Box className="post-job-card-header" sx={{ mb: 1.5 }}>
        <Typography className="post-job-eyebrow" variant="overline" color="primary">
          Company account
        </Typography>
        <Typography variant="h5" component="h2" sx={{ mt: 0.25, mb: 0.5 }}>
          Sign up with your work email
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Companies must register with a work email before posting a job. After verification, you will continue through company info, job details, and payment.
        </Typography>
      </Box>

      <Tabs
        value={mode}
        onChange={(_, value) => {
          setMode(value);
          setError('');
          setSuccess('');
        }}
        sx={{ mb: 1.5, minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 600 } }}
      >
        <Tab value="signup" label="Create account" />
        <Tab value="signin" label="Sign in" />
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 1.25 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 1.25 }}>
          {success}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={1.25}>
          {mode === 'signup' && (
            <>
              <TextField
                label="Contact name"
                name="fullName"
                value={form.fullName}
                onChange={handleChange}
                placeholder="Jane Smith"
                required
              />
              <TextField
                label="Username"
                name="username"
                value={form.username}
                onChange={handleChange}
                placeholder="company-hr"
                inputProps={{ minLength: 3 }}
                required
              />
            </>
          )}

          <TextField
            label="Work email"
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            placeholder="you@yourcompany.com"
            required
          />

          <TextField
            label="Password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={form.password}
            onChange={handleChange}
            placeholder="Minimum 8 characters"
            autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
            inputProps={{ minLength: 8 }}
            required
            helperText={mode === 'signup' ? "Make sure it's at least 8 characters." : undefined}
            FormHelperTextProps={{ sx: { m: 0, mt: 0.35 } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {mode === 'signup' && (
            <TextField
              label="Confirm password"
              name="confirmPassword"
              type={showConfirm ? 'text' : 'password'}
              value={form.confirmPassword}
              onChange={handleChange}
              placeholder="Re-enter your password"
              autoComplete="new-password"
              inputProps={{ minLength: 8 }}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showConfirm ? 'Hide password' : 'Show password'}
                      onClick={() => setShowConfirm((v) => !v)}
                      edge="end"
                      size="small"
                    >
                      {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}

          <Button type="submit" variant="contained" disabled={loading} fullWidth>
            {loading ? 'Please wait...' : mode === 'signup' ? 'Create company account' : 'Sign in & continue'}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}

export default EmployerAuthStep;
